import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { useQuery } from '@sequencehq/api'
import { MINUTES } from '@sequencehq/utils/dates'
import { useEffect, useMemo } from 'react'
import { useSetAtom } from 'jotai'
import { Currency } from '@sequencehq/api/commonEnums'
import {
  hydrateRenewalDataAtom,
  LoadedEligibleSchedule
} from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/executeQuote.atoms'

type UseEligibleSchedulesForCustomer = (args: {
  customerId: string
  currency: Currency | undefined
}) => void

/**
 * Loads the customer's ACTIVE schedules whose currency matches the quote (sorted
 * by start date ascending) and writes them + the loading flag straight into the
 * renewal atoms. The list response carries the full schedule (phases + prices
 * with their structures), so no per-schedule editor load is required.
 *
 * The combined-schedule validation runs the schedule through the cube reducer,
 * which resolves the schedule's currency (from a price) and cross-references its
 * customer — so we also seed the currency and fetch the customer once (all
 * eligible schedules share the quote's customer). The applicability metadata is
 * derived from these in a selector atom; this hook only fetches.
 *
 * Currency is not a first-class field on the schedule, so it is derived from the
 * first price on the schedule (all prices on a schedule share a currency).
 */
export const useEligibleSchedulesForCustomer: UseEligibleSchedulesForCustomer =
  ({ customerId, currency }) => {
    const hydrateData = useSetAtom(hydrateRenewalDataAtom)

    const response = useQuery(
      dashboard20240730Client.getBillingSchedules,
      {
        customerId,
        billingScheduleStatus: 'ACTIVE',
        limit: 100
      },
      {
        enabled: Boolean(customerId) && Boolean(currency),
        staleTime: 1 * MINUTES,
        select: res => res?.items ?? []
      }
    )

    // The reducer nulls a schedule's customerId when the customer isn't present
    // in its data, failing validation — so load the (shared) customer too.
    const customerResponse = useQuery(
      dashboardv99990101Client.getCustomer,
      { id: customerId },
      { enabled: Boolean(customerId), staleTime: 10 * MINUTES }
    )

    const customers = useMemo(
      (): LoadedEligibleSchedule['loaded']['customers'] =>
        customerResponse.data
          ? [{ ...customerResponse.data, aliases: [] }]
          : [],
      [customerResponse.data]
    )

    const activeSchedules = useMemo(
      () =>
        (response.data ?? [])
          .filter(schedule => schedule.prices[0]?.currency === currency)
          .sort((a, b) => a.startDate.localeCompare(b.startDate)),
      [response.data, currency]
    )

    useEffect(() => {
      const loadedSchedules: LoadedEligibleSchedule[] = activeSchedules.map(
        schedule => ({
          schedule,
          // The list schedule carries the phases + full prices the validation
          // reads; the customer and currency are seeded from the queries above,
          // and the remaining loaded fields aren't used by that validation.
          loaded: {
            billingSchedule: schedule,
            prices: schedule.prices,
            /**
             * The product references are fully loaded here because the validation does not require them.
             * However, if we do not load the products into the context, the cleanup functionality will remove
             * the price reference.
             */
            products: schedule.prices.map(price => ({
              id: price.productId,
              name: 'Unresolved product',
              label: 'Unresolved product',
              createdAt: '2026-07-13',
              updatedAt: '2026-07-13'
            })),
            taxRates: [],
            customers,
            integrations: [],
            enabledCurrencies: currency ? [currency] : [],
            // Quote-side, so the schedule editor's account defaults are never
            // read from here.
            billingSettings: null
          }
        })
      )
      hydrateData({
        loadedSchedules,
        isLoadingSchedules: response.isLoading || customerResponse.isLoading
      })
    }, [
      activeSchedules,
      customers,
      currency,
      response.isLoading,
      customerResponse.isLoading,
      hydrateData
    ])
  }
